/**
 * The two-week desk calendar on a home tile (#2640): rows "This" and "Next",
 * columns Monday to Sunday, spiral rings on top, today ringed. A cell is an
 * icon and a day number, no words; its full meaning is the `aria-label`.
 * A cell with a link opens that team's roster at its week.
 */
import {
  CircleCheck,
  CircleDot,
  FileText,
  Loader2,
  type LucideIcon,
  Moon,
  Plane,
  Sun,
} from "lucide-react";
import { Link } from "react-router-dom";

import { cn } from "../../lib/cn";
import type { CalendarCell, CalendarWeek, CellTone } from "./homeOverview";

const WEEKDAY_LETTERS = ["M", "T", "W", "T", "F", "S", "S"] as const;

const TONE_CLASS: Record<CellTone, string> = {
  published: "border-transparent bg-accent-soft text-accent-fg",
  draft: "border-dashed border-accent/60 bg-bg-elevated text-accent-fg",
  solving: "border-transparent bg-warning-soft text-warning",
  none: "border-transparent bg-bg-subtle text-fg-faint",
  day: "border-transparent bg-accent-soft text-fg",
  night: "border-transparent bg-bg-subtle text-fg",
  shift: "border-transparent bg-accent-soft text-fg",
  leave: "border-transparent bg-danger-soft text-danger",
  off: "border-border/60 bg-bg-elevated text-fg-faint",
};

const TONE_ICON: Partial<Record<CellTone, LucideIcon>> = {
  published: CircleCheck,
  draft: FileText,
  solving: Loader2,
  day: Sun,
  night: Moon,
  shift: CircleDot,
  leave: Plane,
};

function Cell({ cell }: { cell: CalendarCell }) {
  const Icon = TONE_ICON[cell.tone];
  const className = cn(
    "relative z-10 flex h-9 min-w-0 flex-col justify-between rounded-md border px-1 py-0.5 text-[10.5px] leading-none",
    TONE_CLASS[cell.tone],
    cell.today && "ring-2 ring-accent ring-offset-0",
    cell.href &&
      "transition-[filter] hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
  );
  const inner = (
    <>
      <span className={cn("text-fg-faint", cell.today && "font-bold text-accent-fg")}>
        {cell.num}
      </span>
      <span className="flex h-3.5 items-center">
        {Icon ? (
          <Icon
            className={cn(
              "h-3.5 w-3.5",
              cell.tone === "solving" && "animate-spin motion-reduce:animate-none",
            )}
            aria-hidden="true"
          />
        ) : null}
      </span>
    </>
  );
  const label = cell.today ? `${cell.label}, today` : cell.label;
  if (cell.href) {
    return (
      <Link
        to={cell.href}
        className={className}
        aria-label={label}
        title={label}
        data-testid="home-cell"
        data-tone={cell.tone}
        data-today={cell.today ? "true" : undefined}
      >
        {inner}
      </Link>
    );
  }
  return (
    <div
      className={className}
      role="img"
      aria-label={label}
      title={label}
      data-testid="home-cell"
      data-tone={cell.tone}
      data-today={cell.today ? "true" : undefined}
    >
      {inner}
    </div>
  );
}

export function MiniCalendar({ weeks }: { weeks: readonly CalendarWeek[] }) {
  return (
    <div
      className="relative rounded-lg border border-border bg-bg-elevated px-2.5 pb-2 pt-3 shadow-sm"
      data-testid="home-mini-calendar"
    >
      <div
        className="pointer-events-none absolute inset-x-3.5 -top-1.5 flex justify-between"
        aria-hidden="true"
      >
        {Array.from({ length: 12 }, (_, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: fixed decorative rings
          <i key={i} className="h-[11px] w-1 rounded-sm border border-fg-faint bg-bg-subtle" />
        ))}
      </div>
      <div
        className="mb-0.5 grid grid-cols-[34px_repeat(7,minmax(0,1fr))] gap-1 text-[10.5px] text-fg-faint"
        aria-hidden="true"
      >
        <span />
        {WEEKDAY_LETTERS.map((d, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: letters repeat (T, S)
          <span key={i} className="text-center">
            {d}
          </span>
        ))}
      </div>
      <div className="flex flex-col gap-1">
        {weeks.map((week) => (
          <div
            key={week.from}
            className="grid grid-cols-[34px_repeat(7,minmax(0,1fr))] gap-1"
            data-testid={`home-week-${week.label.toLowerCase()}`}
          >
            <span className="flex items-center text-[11.5px] font-semibold text-fg-muted">
              {week.label}
            </span>
            {week.cells.map((cell) => (
              <Cell key={cell.iso} cell={cell} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
